import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Layout } from 'containers';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'redux/store';
import { instanceApi } from 'libs';
import { outputCsvData } from './thunk';

type Props = {};

type DataViewOption = {
  label: string;
  value: number;
  fullOnly?: boolean;
};

const DATA_VIEW_OPTIONS: DataViewOption[] = [
  { label: 'ユーザ', value: 1, fullOnly: true },
  { label: '協力業者', value: 2, fullOnly: true },
  { label: '現場', value: 3, fullOnly: true },
  { label: '工事管理', value: 4, fullOnly: true },
  { label: '入退場', value: 5 },
  { label: '入退場管理', value: 6 },
  { label: '入退場履歴補正', value: 7, fullOnly: true },
  { label: '承認', value: 8 },
  { label: '今日のKY表', value: 9 },
  { label: 'KY表チェック', value: 10 },
  { label: '入退場(識別追加)', value: 80 },
  { label: '入退場管理(識別追加)', value: 81 },
  { label: '今日のKY表(識別追加)', value: 82 },
  { label: 'KY表チェック(識別追加)', value: 83 },
  { label: 'RKY提出済の整形', value: 90 },
];

const CsvOutputPage: FC<Props> = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [lwKey, setLwKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [dataViewType, setDataViewType] = useState<number>(1);
  const [fullDataFlag, setFullDataFlag] = useState<number>(1);
  const [date, setDate] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const startedAtRef = useRef<number | null>(null);

  const selectedOption = useMemo(
    () => DATA_VIEW_OPTIONS.find((item) => item.value === dataViewType),
    [dataViewType]
  );

  const isFullOnly = !!selectedOption?.fullOnly;
  const isDateDisabled = isFullOnly || fullDataFlag === 1;

  const handleChangeDataViewType = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = Number(e.target.value);
    const option = DATA_VIEW_OPTIONS.find((item) => item.value === nextValue);

    setDataViewType(nextValue);

    if (option?.fullOnly) {
      setFullDataFlag(1);
      setDate('');
    }
  }, []);

  const handleChangeFullDataFlag = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(e.target.value);
    setFullDataFlag(nextValue);

    if (nextValue === 1) {
      setDate('');
    }
  }, []);

  const validate = useCallback(() => {
    if (!lwKey.trim()) {
      toast.error('パスワードを入力してください。');
      return false;
    }

    if (!selectedOption) {
      toast.error('出力対象を選択してください。');
      return false;
    }

    if (!isFullOnly && fullDataFlag === 0 && !date) {
      toast.error('月指定の場合は対象月を選択してください。');
      return false;
    }

    return true;
  }, [lwKey, selectedOption, isFullOnly, fullDataFlag, date]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);
      setElapsedMs(null);
      startedAtRef.current = Date.now();

      const resultAction = await dispatch(
        outputCsvData({
          data_view_type: dataViewType,
          full_data_flag: fullDataFlag,
          date: fullDataFlag === 0 ? date : undefined,
          lw_key: lwKey,
        })
      );

      const finishedAt = Date.now();

      if (outputCsvData.rejected.match(resultAction)) {
        const payload = resultAction.payload as { message?: string };
        toast.error(payload?.message || 'CSV出力に失敗しました。');
        return;
      }

      const payload = resultAction.payload as {
        files?: { fileName: string; rowCount: number }[];
      };

      if (!payload?.files?.length) {
        setElapsedMs(finishedAt - (startedAtRef.current ?? finishedAt));
        toast.info('対象データがありませんでした。');
        return;
      }

      setElapsedMs(finishedAt - (startedAtRef.current ?? finishedAt));
      toast.success(`CSV出力が完了しました。（${payload.files.length}ファイル）`);

      for (const file of payload.files) {
        const res = await instanceApi.get('/csv-output/download', {
          params: { fileName: file.fileName },
          responseType: 'blob',
        });
        const blobUrl = window.URL.createObjectURL(res.data);
        try {
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = file.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } finally {
          window.URL.revokeObjectURL(blobUrl);
        }
      }
    } catch (error) {
      toast.error('CSV出力に失敗しました。');
    } finally {
      setSubmitting(false);
      startedAtRef.current = null;
    }
  }, [validate, dispatch, dataViewType, fullDataFlag, date, lwKey]);

  return (
    <>
      <Layout left right showButtonBranchSelect />

      <div style={styles.wrapper}>
        <div style={styles.card}>
          <h2 style={styles.title}>CSV出力</h2>

          <div style={styles.formRow}>
            <label style={styles.label}>パスワード</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={lwKey}
                onChange={(e) => setLwKey(e.target.value)}
                style={styles.passwordInput}
                placeholder="パスワードを入力"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                style={styles.eyeButton}
                aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
              >
                {showPassword ? (
                  <EyeOutlined style={styles.eyeIcon} />
                ) : (
                  <EyeInvisibleOutlined style={{ ...styles.eyeIcon, transform: 'scaleX(-1)' }} />
                )}
              </button>
            </div>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>出力対象</label>
            <select value={dataViewType} onChange={handleChangeDataViewType} style={styles.select}>
              {DATA_VIEW_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                  {item.fullOnly ? '（全量のみ）' : ''}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>出力方法</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="full_data_flag"
                  value={1}
                  checked={fullDataFlag === 1}
                  onChange={handleChangeFullDataFlag}
                />
                全量出力
              </label>

              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="full_data_flag"
                  value={0}
                  checked={fullDataFlag === 0}
                  onChange={handleChangeFullDataFlag}
                  disabled={isFullOnly}
                />
                月指定出力
              </label>
            </div>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>対象月</label>
            <input
              type="month"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={styles.input}
              disabled={isDateDisabled}
            />
          </div>

          <div style={styles.noteArea}>
            {isFullOnly ? (
              <div style={styles.noteText}>この出力対象は全量出力のみ対応しています。</div>
            ) : (
              <div style={styles.noteText}>月指定出力時は対象月を選択してください。</div>
            )}
          </div>

          <div style={styles.buttonArea}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={styles.button}
            >
              {submitting ? '出力中...' : '出力開始'}
            </button>

            <div style={styles.elapsedArea}>
              {elapsedMs !== null ? `処理時間: ${(elapsedMs / 1000).toFixed(2)}秒` : ''}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    padding: '24px',
  },
  card: {
    background: '#fff',
    border: '1px solid #ddd',
    padding: '24px',
    maxWidth: '720px',
  },
  title: {
    margin: '0 0 24px 0',
    fontSize: '20px',
    fontWeight: 700,
  },
  formRow: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 700,
  },
  input: {
    width: '100%',
    maxWidth: '360px',
    height: '36px',
    padding: '0 10px',
    fontSize: '14px',
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: '360px',
  },
  passwordInput: {
    width: '100%',
    height: '36px',
    padding: '0 40px 0 10px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    top: '50%',
    right: '10px',
    transform: 'translateY(-50%)',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eyeIcon: {
    fontSize: '16px',
    color: '#666',
  },
  select: {
    width: '100%',
    maxWidth: '360px',
    height: '36px',
    padding: '0 10px',
    fontSize: '14px',
  },
  radioGroup: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  noteArea: {
    marginTop: '8px',
    marginBottom: '24px',
  },
  noteText: {
    fontSize: '12px',
    color: '#666',
  },
  buttonArea: {
    marginTop: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  button: {
    minWidth: '140px',
    height: '40px',
    padding: '0 16px',
    cursor: 'pointer',
  },
  elapsedArea: {
    minHeight: '20px',
    fontSize: '14px',
    color: '#333',
  },
};

export default CsvOutputPage;
